import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Contract,
  ZeroAddress,
  formatUnits,
  parseUnits,
  getAddress,
  isAddress,
} from "ethers";
import { useActiveAccount, useActiveWalletChain } from "thirdweb/react";
import { ethers6Adapter } from "thirdweb/adapters/ethers6";
import { thirdwebClient, bscChain } from "../config/thirdweb";

import PresaleContractAddress from "./contractsData/PreSaleContract-address.json";
import PresaleContract from "./contractsData/PreSaleContract.json";
import USDTContractAddress from "./contractsData/USDTToken-address.json";
import USDCContractAddress from "./contractsData/USDCToken-address.json";
import saleTokenAddress from "./contractsData/SaleToken-address.json";
import TokenContractABI from "./contractsData/USDTToken.json";
import { BRAND } from "../config/brand";
import { Store } from "./StoreContext";
import { NotificationContext } from "./NotificationContext";
import {
  connectReferralWallet,
  getReferralProfile,
  syncReferralPurchase,
} from "../services/referralApi";
import {
  TOKEN_DECIMALS,
  getProviderPresaleContract,
  normalizeReferrer,
  getContractErrorMessage,
  getReadProvider,
} from "./presaleUtils";

const expectedChainId = BigInt(import.meta.env.VITE_BSC_CHAIN_ID || "56");
const explorerBaseUrl =
  import.meta.env.VITE_BLOCK_EXPLORER_URL ||
  (expectedChainId === 97n
    ? "https://testnet.bscscan.com"
    : "https://bscscan.com");
const pendingSyncStorageKey = "pending_referral_purchase_syncs";

const readPendingSyncs = () => {
  try {
    const value = JSON.parse(localStorage.getItem(pendingSyncStorageKey) || "[]");
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
};

const queuePendingSync = (transactionHash) => {
  const pending = new Set(readPendingSyncs());
  pending.add(transactionHash);
  localStorage.setItem(pendingSyncStorageKey, JSON.stringify([...pending]));
};

const removePendingSync = (transactionHash) => {
  const pending = readPendingSyncs().filter((hash) => hash !== transactionHash);
  localStorage.setItem(pendingSyncStorageKey, JSON.stringify(pending));
};

const fallbackNotify = {
  error: (message) => console.error(message),
  info: (message) => console.info(message),
  success: (message) => console.log(message),
};

const approveExactAmount = async ({
  tokenContract,
  spender,
  currentAllowance,
  requiredAmount,
}) => {
  if (currentAllowance >= requiredAmount) return;

  try {
    const transaction = await tokenContract.approve(spender, requiredAmount);
    await transaction.wait();
  } catch (error) {
    console.warn("Direct approval failed; trying zero-reset approval.", error);

    const resetTransaction = await tokenContract.approve(spender, 0n);
    await resetTransaction.wait();

    const transaction = await tokenContract.approve(spender, requiredAmount);
    await transaction.wait();
  }
};

export const StoreProvider = ({ children }) => {
  const account = useActiveAccount();
  const activeChain = useActiveWalletChain();
  const address = account?.address;
  const isConnected = Boolean(address);
  const notificationContext = useContext(NotificationContext);
  const notify = notificationContext?.notify || fallbackNotify;
  const hideNotification = notificationContext?.hideNotification;

  const [loader, setloader] = useState(false);
  const [transactionHash, setTransactionHash] = useState("");
  const [showTransactionSucess, setShowTransactionSucess] = useState(false);
  const [userDatabaseData, setUserDatabaseData] = useState({
    currentPrice: "0",
    saleAllocation: "0",
    totalRaised: "0",
    totalBuyerTokensSold: "0",
    totalReferralTokensPaid: "0",
    totalTokensDistributed: "0",
    remainingAllocation: "0",
    contractInventory: "0",
    referralRewardBps: "0",
    referredBy: null,
    referralAddress: "",
    referralProfileLoaded: false,
    totalReferrals: 0,
    receivedReferralAmount: "0",
    paused: true,
    loaded: false,
  });

  const applyReferralProfile = useCallback((profile) => {
    if (!profile) return;
    setUserDatabaseData((current) => ({
      ...current,
      ...profile,
      referralProfileLoaded: true,
    }));
  }, []);

  const refreshReferralProfile = useCallback(async () => {
    if (!isConnected || !address) return null;

    try {
      const profile = await getReferralProfile(address);
      applyReferralProfile(profile);
      return profile;
    } catch (error) {
      console.warn("Referral profile is temporarily unavailable:", error.message);
      return null;
    }
  }, [address, applyReferralProfile, isConnected]);

  useEffect(() => {
    let cancelled = false;

    if (!isConnected || !address) {
      queueMicrotask(() => {
        if (!cancelled) {
          setUserDatabaseData((current) => ({
            ...current,
            referredBy: null,
            referralAddress: "",
            referralProfileLoaded: false,
            totalReferrals: 0,
            receivedReferralAmount: "0",
          }));
        }
      });
      return () => {
        cancelled = true;
      };
    }

    connectReferralWallet(address)
      .then(async (profile) => {
        if (!cancelled) applyReferralProfile(profile);

        for (const transactionHash of readPendingSyncs()) {
          try {
            const syncedPurchase = await syncReferralPurchase(transactionHash);
            removePendingSync(transactionHash);
            if (!cancelled && syncedPurchase.profile?.walletAddress === profile.walletAddress) {
              applyReferralProfile(syncedPurchase.profile);
            }
          } catch (error) {
            console.warn("Pending referral sync will be retried:", error.message);
          }
        }
      })
      .catch((error) => {
        console.warn("Referral profile is temporarily unavailable:", error.message);
      });

    return () => {
      cancelled = true;
    };
  }, [address, applyReferralProfile, isConnected]);

  const refreshPresaleData = useCallback(async () => {
    try {
      const provider = getReadProvider();
      const presale = new Contract(
        PresaleContractAddress.address,
        PresaleContract.abi,
        provider,
      );
      const saleTokenContract = new Contract(
        saleTokenAddress.address,
        TokenContractABI.abi,
        provider,
      );

      const [
        currentPrice,
        saleAllocation,
        totalRaised,
        totalBuyerTokensSold,
        totalReferralTokensPaid,
        totalTokensDistributed,
        remainingAllocation,
        referralRewardBps,
        paused,
        contractInventory,
      ] = await Promise.all([
        presale.salePrice(),
        presale.saleAllocation(),
        presale.totalRaised(),
        presale.totalBuyerTokensSold(),
        presale.totalReferralStablePaid(),
        presale.totalTokensDistributed(),
        presale.remainingSaleAllocation(),
        presale.referralRewardBps(),
        presale.paused(),
        saleTokenContract.balanceOf(PresaleContractAddress.address),
      ]);
      setUserDatabaseData((current) => ({
        ...current,
        currentPrice: currentPrice.toString(),
        saleAllocation: saleAllocation.toString(),
        totalRaised: totalRaised.toString(),
        totalBuyerTokensSold: totalBuyerTokensSold.toString(),
        totalReferralTokensPaid: totalReferralTokensPaid.toString(),
        totalTokensDistributed: totalTokensDistributed.toString(),
        remainingAllocation: remainingAllocation.toString(),
        contractInventory: contractInventory.toString(),
        referralRewardBps: referralRewardBps.toString(),
        paused: Boolean(paused),
        loaded: true,
      }));

      return true;
    } catch (error) {
      console.error("Unable to load presale state:", error);
      return false;
    }
  }, []);

  const quoteStablePurchase = useCallback(
    async (humanPaymentAmount, buyerAddress = ZeroAddress, suppliedReferrer = ZeroAddress) => {
      if (!humanPaymentAmount || Number(humanPaymentAmount) <= 0) {
        return {
          paymentAmount: 0n,
          effectiveReferrer: ZeroAddress,
          buyerTokens: 0n,
          referralTokens: 0n,
          totalTokensRequired: 0n,
          formattedBuyerTokens: "0",
          formattedReferralTokens: "0",
        };
      }

      const paymentAmount = parseUnits(
        humanPaymentAmount.toString(),
        TOKEN_DECIMALS,
      );
      const presale = getProviderPresaleContract();
      const safeBuyer =
        buyerAddress && isAddress(buyerAddress)
          ? getAddress(buyerAddress)
          : ZeroAddress;
      const safeReferrer = normalizeReferrer(suppliedReferrer, safeBuyer);

      const buyerTokens = await presale.quoteTokensForPayment(paymentAmount);

      let effectiveReferrer;
      let referralTokens;
      let totalTokensRequired;

      // quotePurchase validates referral rules and includes referral bonus tokens.
      [
        effectiveReferrer,
        ,
        referralTokens,
        totalTokensRequired,
      ] = await presale.quotePurchase(
        paymentAmount,
        safeBuyer,
        safeReferrer,
      );

      return {
        paymentAmount,
        effectiveReferrer,
        buyerTokens,
        referralTokens,
        totalTokensRequired,
        formattedBuyerTokens: formatUnits(buyerTokens, TOKEN_DECIMALS),
        formattedReferralTokens: formatUnits(referralTokens, TOKEN_DECIMALS),
      };
    },
    [],
  );

  const BuyWithUSDTandUSDC = useCallback(
    async (humanPaymentAmount, isUSDT, suppliedReferrer = ZeroAddress) => {
      if (!isConnected || !address || !account) {
        notify.error("Please connect your wallet.");
        return false;
      }

      if (!humanPaymentAmount || Number(humanPaymentAmount) <= 0) {
        notify.error("Please enter a valid payment amount.");
        return false;
      }

      setloader(true);
      setTransactionHash("");

      try {
        if (BigInt(activeChain?.id || 0) !== expectedChainId) {
          throw new Error(
            `Wrong network. Switch your wallet to BSC chain ID ${expectedChainId.toString()}.`,
          );
        }

        const signer = await ethers6Adapter.signer.toEthers({
          client: thirdwebClient,
          chain: bscChain,
          account,
        });
        const presale = new Contract(
          PresaleContractAddress.address,
          PresaleContract.abi,
          signer,
        );
        const stablecoinAddress = isUSDT
          ? USDTContractAddress.address
          : USDCContractAddress.address;
        const stablecoin = new Contract(
          stablecoinAddress,
          TokenContractABI.abi,
          signer,
        );

        const paymentAmount = parseUnits(
          humanPaymentAmount.toString(),
          TOKEN_DECIMALS,
        );
        const effectiveInputReferrer = normalizeReferrer(
          suppliedReferrer,
          address,
        );

        const [paused, balance, currentAllowance, quote] = await Promise.all([
          presale.paused(),
          stablecoin.balanceOf(address),
          stablecoin.allowance(address, PresaleContractAddress.address),
          quoteStablePurchase(
            humanPaymentAmount,
            address,
            effectiveInputReferrer,
          ),
        ]);

        if (paused) {
          throw new Error("Presale is currently paused.");
        }

        if (quote.buyerTokens <= 0n) {
          throw new Error("The calculated sale-token amount is zero.");
        }

        if (balance < paymentAmount) {
          throw new Error(
            `You do not have enough ${isUSDT ? "USDT" : "USDC"}.`,
          );
        }

        const [remainingAllocation, inventory] = await Promise.all([
          presale.remainingSaleAllocation(),
          new Contract(
            saleTokenAddress.address,
            TokenContractABI.abi,
            getReadProvider(),
          ).balanceOf(PresaleContractAddress.address),
        ]);

        if (quote.totalTokensRequired > remainingAllocation) {
          throw new Error("The remaining presale allocation is insufficient.");
        }

        if (quote.totalTokensRequired > inventory) {
          throw new Error("The presale contract does not have enough tokens.");
        }

        await approveExactAmount({
          tokenContract: stablecoin,
          spender: PresaleContractAddress.address,
          currentAllowance,
          requiredAmount: paymentAmount,
        });

        const purchaseTransaction = await presale.buyWithStable(
          paymentAmount,
          isUSDT,
          quote.buyerTokens,
          ZeroAddress
          // quote.effectiveReferrer,
        );

        notify.info("Transaction submitted. Waiting for confirmation...");
        const receipt = await purchaseTransaction.wait();

        if (!receipt || receipt.status !== 1) {
          throw new Error("Transaction was not confirmed successfully.");
        }

        setTransactionHash(
          `${explorerBaseUrl}/tx/${purchaseTransaction.hash}`,
        );
        hideNotification?.();
        setShowTransactionSucess(true);
        await refreshPresaleData();

        try {
          const syncedPurchase = await syncReferralPurchase(
            purchaseTransaction.hash,
          );
          removePendingSync(purchaseTransaction.hash);
          applyReferralProfile(syncedPurchase.profile);
        } catch (syncError) {
          // The blockchain purchase is already final; backend sync can be retried safely.
          queuePendingSync(purchaseTransaction.hash);
          console.warn("Purchase completed, but referral sync is pending:", syncError.message);
        }

        return true;
      } catch (error) {
        setTransactionHash("");
        notify.error(getContractErrorMessage(error));
        return false;
      } finally {
        setloader(false);
      }
    },
    [
      account,
      activeChain,
      address,
      applyReferralProfile,
      isConnected,
      hideNotification,
      notify,
      quoteStablePurchase,
      refreshPresaleData,
    ],
  );

  const addTokenInMetamask = useCallback(async () => {
    try {
      if (!isConnected || !account) {
        notify.error("Please connect your wallet.");
        return false;
      }

      if (!account.watchAsset) {
        notify.error("Your wallet does not support adding custom tokens.");
        return false;
      }

      const wasAdded = await account.watchAsset({
        type: "ERC20",
        options: {
          address: BRAND.tokenAddress,
          symbol: BRAND.tokenSymbol,
          decimals: Number(BRAND.tokenDecimals || TOKEN_DECIMALS),
          image: `${BRAND.domain}${BRAND.logoPath}`,
        },
      });

      if (wasAdded) notify.success("Token added to your wallet.");
      return Boolean(wasAdded);
    } catch (error) {
      notify.error(getContractErrorMessage(error));
      return false;
    }
  }, [account, isConnected, notify]);

  const value = useMemo(
    () => ({
      loader,
      setloader,
      transactionHash,
      setTransactionHash,
      showTransactionSucess,
      setShowTransactionSucess,
      userDatabaseData,
      getProviderPresaleContract,
      quoteStablePurchase,
      BuyWithUSDTandUSDC,
      addTokenInMetamask,
      refreshPresaleData,
      refreshReferralProfile,
    }),
    [
      BuyWithUSDTandUSDC,
      addTokenInMetamask,
      loader,
      quoteStablePurchase,
      refreshPresaleData,
      refreshReferralProfile,
      showTransactionSucess,
      transactionHash,
      userDatabaseData,
    ],
  );

  return <Store.Provider value={value}>{children}</Store.Provider>;
};
